
import React from 'react';

const TermsContent_en: React.FC = () => (
    <div className="space-y-4 text-sm leading-relaxed text-slate-700" dir="ltr">
        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">1. Recipe Ownership & Attribution</h3>
            <p>
                By publishing content on mealPai, you acknowledge and agree that any recipe added from an external source (including but not limited to websites, cookbooks, or blogs) that is identical or substantially similar to the original source <strong>must include a direct reference link (URL) or citation</strong> to the original creator if the recipe is marked as "Public".
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">2. User Responsibility & Liability</h3>
            <p>
                Public recipes that are copied from an external source without appropriate credit or reference may constitute a violation of intellectual property rights.
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>
                    <strong>User Accountability:</strong> The user publishing the content is solely responsible and accountable for ensuring they have the right to share the content and for providing necessary attribution.
                </li>
                <li>
                    <strong>Limitation of Liability:</strong> mealPai, its operators, employees, volunteers, and affiliates (paid or unpaid) will <strong>not be held accountable or liable</strong> for any recipes copied, published, or distributed by users without proper credit or in violation of copyright laws.
                </li>
            </ul>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">3. Modifications & Fair Use</h3>
            <p>
                Users are encouraged to suggest changes, adaptions, or improvements to recipes. However, such suggestions and published modifications must remain within the bounds of applicable copyright laws. Substantial transformation of a recipe does not automatically negate the requirement for attribution if the core work is derived from a protected source.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">4. Prohibited Content & Conduct</h3>
            <p>
                Users are strictly prohibited from publishing content that includes foul, offensive, racist, brutal, hate speech, or otherwise inappropriate language.
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>
                    <strong>Zero Tolerance:</strong> mealPai maintains a zero-tolerance policy towards harassment, hate speech, and offensive content.
                </li>
                <li>
                    <strong>Account Deactivation:</strong> Any user found to be responsible for publishing such content will be held personally accountable. mealPai reserves the right to <strong>immediately deactivate the account</strong> of any user violating this policy, without prior notice.
                </li>
            </ul>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">5. Content Removal</h3>
            <p>
                mealPai reserves the right, but does not assume the obligation, to remove any content that is reported or discovered to be in violation of these terms or applicable intellectual property laws, without prior notice to the user.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">6. Pricing & Subscriptions</h3>
            <p>
                Currently, mealPai is provided free of charge for testing and personal use. However, we reserve the right to introduce paid subscription plans or service fees in the future. Access to certain premium features or higher usage limits may require a paid subscription. Users will be notified in advance of any mandatory changes to the pricing model.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">7. Data Persistence & Loss</h3>
            <p>
                While we make reasonable efforts to maintain the security and integrity of your data, mealPai is provided "as is" and "as available". We <strong>expressly disclaim any responsibility or liability for the loss of data</strong> (including recipes, meal plans, or account history) occurring over time due to technical errors, system migrations, database failures, or account deletion.
            </p>
            <p className="mt-2">
                Users are strongly encouraged to periodically use the <strong>"Export Data"</strong> feature available in the menu to keep personal backups of their information.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">8. Use of Artificial Intelligence (AI) and Disclaimer</h3>
            <p>
                The app (mealPai) utilizes Artificial Intelligence (AI) technologies for recipe analysis, ingredient extraction, allergy warnings, and user-suited meal analysis. Please be advised that this technology is not immune to errors and may sometimes generate incorrect, incomplete, or inaccurate information.
            </p>
            <p className="mt-2">
                All AI-generated content is provided as a recommendation and for informational purposes only. Users must exercise caution and use their own independent judgment. It is the user&apos;s sole responsibility to manually review and verify all ingredients and allergy warnings (especially in cases of severe allergies or strict dietary restrictions) before preparing or consuming any food. Reliance on this information is strictly at the user&apos;s own risk.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">9. Not a Medical or Healthcare Provider</h3>
            <p>
                mealPai is a culinary management and household planning tool, not a healthcare provider, dietitian, or medical professional. The information provided through the mealPai application—including but not limited to recipes, meal plans, grocery lists, and dietary tags—is for informational and organizational purposes only. Use of the application does not create a doctor-patient or consultant-client relationship between you and mealPai.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">10. Measurements and Nutritional Estimates</h3>
            <p>
                All nutritional values (such as calories, macronutrients, and micronutrients), preparation times, and ingredient measurements provided within the application are automated estimates. These estimates may be generated by third-party artificial intelligence or sourced from user-generated content. mealPai makes no guarantees regarding the accuracy, completeness, or reliability of these estimates.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">11. Strict User Responsibility for Allergies and Dietary Restrictions</h3>
            <p>
                mealPai does not guarantee that any recipe, meal plan, or shopping list is free of allergens or compliant with specific dietary restrictions (e.g., gluten-free, keto, vegan).
            </p>
            <p className="mt-2">
                <strong>Verification:</strong> It is the user&apos;s strict responsibility to read all physical ingredient labels, verify sourcing, and ensure that all food purchased and prepared is safe for their specific household and allergy requirements.
            </p>
            <p className="mt-2">
                <strong>No Liability:</strong> mealPai assumes no liability for adverse reactions, foodborne illnesses, cross-contamination, or any health issues resulting from the preparation or consumption of meals planned through the application.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">12. Consultation with Qualified Professionals</h3>
            <p>
                If you have specific health goals (such as weight management or athletic performance), severe food allergies, or medical conditions (such as diabetes or celiac disease), you should seek the advice of a licensed physician, registered dietitian, or qualified healthcare professional. While mealPai allows users to connect with their personal consultants, mealPai itself does not endorse or validate the medical efficacy of any plan created within the app.
            </p>
        </section>

        <section>
            <h3 className="text-lg font-bold text-slate-900 mb-2">13. Contact for Urgent or Life-Threatening Issues</h3>
            <p>
                If you experience a problem, error, or concern related to your use of the application—especially where the situation may be life-threatening or requires urgent attention—you are advised to contact mealPai through the <strong>&quot;Contact&quot;</strong> option in the menu (email) or through the <strong>&quot;Suggestion Box&quot;</strong> within the app.
            </p>
            <p className="mt-2">
                <strong>Important:</strong> mealPai is not an emergency service. In a medical emergency or life-threatening situation, contact emergency services or a qualified healthcare professional immediately. Do not rely on the application alone.
            </p>
        </section>
    </div>
);

export default TermsContent_en;
